import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { RefreshCw, AlertCircle, CheckCircle, AlertTriangle, Zap, ChevronUp, ChevronDown, ChevronsUpDown, Trash2, LayoutGrid, X } from "lucide-react";
import GenerateModal from "./GenerateModal";
import UniversalGenerateModal from "./UniversalGenerateModal";
import BatchGenerateModal from "./BatchGenerateModal";

const API = process.env.REACT_APP_BACKEND_URL;

function apiFetch(path, token, opts = {}) {
  return fetch(`${API}/api${path}`, {
    method: opts.method || "GET",
    headers: { Authorization: `Bearer ${token}` },
    ...opts,
  });
}

// Literature / Poetry sub-topics — Pool A/B Gen button is suppressed for these.
// WarmUp Gen button remains available. This list is intentionally hardcoded in the UI.
const LIT_KEYWORDS = [
  "comprehension", "passage", "poem", "poetry", "story", "prose",
  "theme", "character", "inference", "narrative",
];
function isLitExempt(row) {
  const st = (row.sub_topic || "").toLowerCase();
  return (
    LIT_KEYWORDS.some((k) => st.includes(k)) ||
    row.topic_type === "Literature" ||
    ["Sahitya", "Literature"].includes(row.track)
  );
}

// ── Health badge ──────────────────────────────────────────────────────────────

function HealthBadge({ health, size = "sm" }) {
  const cfg = {
    green: { bg: "#d1fae5", color: "#065f46", label: "Healthy" },
    amber: { bg: "#fef3c7", color: "#92400e", label: "Needs Attention" },
    red:   { bg: "#fee2e2", color: "#991b1b", label: "Critical" },
  }[health] || { bg: "#f3f4f6", color: "#374151", label: health };
  const Icon = health === "green" ? CheckCircle : health === "amber" ? AlertTriangle : AlertCircle;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full font-semibold ${size === "lg" ? "text-sm" : "text-xs"}`}
      style={{ background: cfg.bg, color: cfg.color }}
    >
      <Icon size={size === "lg" ? 14 : 12} />
      {cfg.label}
    </span>
  );
}

// ── WarmUp cell with Gen button ───────────────────────────────────────────────

function WarmUpCell({ count, gap, onGenerate }) {
  return (
    <div className="flex items-center gap-1.5 justify-center">
      <span
        className="inline-block px-2.5 py-1 rounded-md text-xs font-semibold"
        style={gap ? { background: "#fee2e2", color: "#991b1b" } : { background: "#d1fae5", color: "#065f46" }}
      >
        {count}{gap && " ⚑"}
      </span>
      {gap && onGenerate && (
        <button
          onClick={onGenerate}
          title="Generate WarmUp questions via Claude"
          className="flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold text-white transition-colors"
          style={{ background: "#2563eb" }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "#1d4ed8")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "#2563eb")}
          data-testid="generate-warmup-btn"
        >
          <Zap size={10} /> Gen
        </button>
      )}
    </div>
  );
}

// ── Pool A / B cell with Gen button ──────────────────────────────────────────

function PoolCell({ count, gap, onGenerate, color = "#7c3aed", hoverColor = "#6d28d9", testId }) {
  return (
    <div className="flex items-center gap-1.5 justify-center">
      <span
        className="inline-block px-2.5 py-1 rounded-md text-xs font-semibold"
        style={gap ? { background: "#fef3c7", color: "#92400e" } : { background: "#f3f4f6", color: "#374151" }}
      >
        {count}{gap && " ⚑"}
      </span>
      {gap && onGenerate && (
        <button
          onClick={onGenerate}
          title={`Generate questions via Claude`}
          className="flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold text-white transition-colors"
          style={{ background: color }}
          onMouseEnter={(e) => (e.currentTarget.style.background = hoverColor)}
          onMouseLeave={(e) => (e.currentTarget.style.background = color)}
          data-testid={testId}
        >
          <Zap size={10} /> Gen
        </button>
      )}
    </div>
  );
}

// ── Cold Pool cell (unchanged) ────────────────────────────────────────────────

function ColdPoolCell({ count, expected, ok, onGenerate }) {
  if (expected === 0) return <span className="text-xs text-gray-400">—</span>;
  return (
    <div className="flex items-center gap-1.5 justify-center">
      <span
        className="inline-block px-2.5 py-1 rounded-md text-xs font-semibold"
        style={ok ? { background: "#d1fae5", color: "#065f46" } : { background: "#fef3c7", color: "#92400e" }}
        title={ok ? `${count} / ${expected} required` : `Only ${count} of ${expected} required`}
      >
        {count}/{expected}
        {!ok && " ⚑"}
      </span>
      {!ok && onGenerate && (
        <button
          onClick={onGenerate}
          title="Generate Cold Pool questions via Claude"
          className="flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold text-white transition-colors"
          style={{ background: "#059669" }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "#047857")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "#059669")}
          data-testid="generate-cold-pool-btn"
        >
          <Zap size={10} /> Gen
        </button>
      )}
    </div>
  );
}

// ── Sub-topic table ───────────────────────────────────────────────────────────

const HEALTH_RANK = { red: 0, amber: 1, green: 2 };

const SORT_COLS = {
  subject:    (r) => r.subject || "",
  topic:      (r) => r.topic   || "",
  sub_topic:  (r) => r.sub_topic || "",
  warmup:     (r) => r.warmup  ?? 0,
  pool_a:     (r) => r.pool_a  ?? 0,
  pool_b:     (r) => r.pool_b  ?? 0,
  cold_pool:  (r) => r.expected_cold > 0 ? r.cold_pool / r.expected_cold : 1,
  health:     (r) => HEALTH_RANK[r.health] ?? 2,
};

function SortIcon({ col, sortCol, sortDir }) {
  if (sortCol !== col) return <ChevronsUpDown size={11} className="ml-1 opacity-40 inline"/>;
  return sortDir === "asc"
    ? <ChevronUp   size={11} className="ml-1 text-gray-700 inline"/>
    : <ChevronDown size={11} className="ml-1 text-gray-700 inline"/>;
}

function SubTopicTable({ subTopics, onGenerate }) {
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  const handleSort = (col) => {
    if (sortCol === col) {
      setSortDir((d) => d === "asc" ? "desc" : "asc");
    } else {
      setSortCol(col);
      setSortDir(col === "health" ? "asc" : "asc"); // health asc = worst first
    }
  };

  const sortedRows = sortCol
    ? [...subTopics].sort((a, b) => {
        const va = SORT_COLS[sortCol](a);
        const vb = SORT_COLS[sortCol](b);
        const cmp = typeof va === "string"
          ? va.localeCompare(vb, undefined, { sensitivity: "base" })
          : va - vb;
        return sortDir === "asc" ? cmp : -cmp;
      })
    : subTopics;

  const thBase = {
    padding: "10px 14px",
    fontSize: 11,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: "#6b7280",
    borderBottom: "1px solid #e5e7eb",
    background: "#f9fafb",
    whiteSpace: "nowrap",
    userSelect: "none",
  };

  const Th = ({ col, align = "left", children }) => {
    const sortable = col && SORT_COLS[col];
    return (
      <th
        style={{
          ...thBase,
          textAlign: align,
          cursor: sortable ? "pointer" : "default",
        }}
        onClick={sortable ? () => handleSort(col) : undefined}
        data-testid={col ? `sort-col-${col}` : undefined}
        title={sortable ? `Sort by ${col.replace("_", " ")}` : undefined}
      >
        {children}
        {sortable && <SortIcon col={col} sortCol={sortCol} sortDir={sortDir} />}
      </th>
    );
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="dataset-subtopic-table">
        <thead>
          <tr>
            <Th col="subject">Subject</Th>
            <Th col="topic">Topic</Th>
            <Th col="sub_topic">Sub-Topic</Th>
            <Th col="warmup"  align="center">WarmUp</Th>
            <Th col="pool_a"  align="center">Pool A</Th>
            <Th col={null}    align="center">A Traps</Th>
            <Th col="pool_b"  align="center">Pool B</Th>
            <Th col={null}    align="center">B Traps</Th>
            <Th col="cold_pool" align="center">Cold Pool</Th>
            <Th col="health"  align="center">Health</Th>
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((row, i) => {
            const litExempt = isLitExempt(row);
            return (
              <tr
                key={i}
                style={{
                  borderBottom: "1px solid #f3f4f6",
                  background:
                    row.health === "red"   ? "rgba(239,68,68,0.03)" :
                    row.health === "amber" ? "rgba(245,158,11,0.03)" : "white",
                }}
              >
                <td className="px-3.5 py-3 text-gray-700 font-medium text-xs">{row.subject}</td>
                <td className="px-3.5 py-3 text-gray-600 text-xs">{row.topic}</td>
                <td className="px-3.5 py-3 text-xs" style={{ maxWidth: 220 }}>
                  <span className="text-gray-800 font-medium leading-tight">{row.sub_topic}</span>
                  {litExempt && (
                    <span className="ml-1.5 text-xs px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-600 font-medium">Lit</span>
                  )}
                </td>

                {/* WarmUp */}
                <td className="px-3.5 py-3 text-center">
                  <WarmUpCell
                    count={row.warmup}
                    gap={row.warmup_gap}
                    onGenerate={onGenerate ? () => onGenerate(row, "WarmUp") : null}
                  />
                </td>

                {/* Pool A */}
                <td className="px-3.5 py-3 text-center">
                  <PoolCell
                    count={row.pool_a}
                    gap={row.pool_a_gap}
                    onGenerate={(!litExempt && onGenerate) ? () => onGenerate(row, "Pool A") : null}
                    color="#7c3aed"
                    hoverColor="#6d28d9"
                    testId="generate-pool-a-btn"
                  />
                </td>
                <td className="px-3.5 py-3 text-center">
                  {row.pool_a_trap > 0 ? (
                    <span className="text-xs font-medium text-purple-700">{row.pool_a_trap}</span>
                  ) : (
                    <span className="text-xs text-gray-300">—</span>
                  )}
                </td>

                {/* Pool B */}
                <td className="px-3.5 py-3 text-center">
                  <PoolCell
                    count={row.pool_b}
                    gap={row.pool_b_gap}
                    onGenerate={(!litExempt && onGenerate) ? () => onGenerate(row, "Pool B") : null}
                    color="#b45309"
                    hoverColor="#92400e"
                    testId="generate-pool-b-btn"
                  />
                </td>
                <td className="px-3.5 py-3 text-center">
                  {row.pool_b_trap > 0 ? (
                    <span className="text-xs font-medium text-purple-700">{row.pool_b_trap}</span>
                  ) : (
                    <span className="text-xs text-gray-300">—</span>
                  )}
                </td>

                {/* Cold Pool */}
                <td className="px-3.5 py-3 text-center">
                  <ColdPoolCell
                    count={row.cold_pool}
                    expected={row.expected_cold}
                    ok={row.cold_ratio_ok}
                    onGenerate={onGenerate ? () => onGenerate(row, "Cold Pool") : null}
                  />
                </td>

                <td className="px-3.5 py-3 text-center">
                  <HealthBadge health={row.health} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Grade accordion section ───────────────────────────────────────────────────

function GradeSection({ gradeData, onGenerate }) {
  const [open, setOpen] = useState(true);
  const { grade, health, sub_topics } = gradeData;
  const gradeNum = grade ? grade.replace("Grade ", "G") : "Unknown";

  const subjectCount  = [...new Set(sub_topics.map((s) => s.subject))].length;
  const warmupGaps    = sub_topics.filter((s) => s.warmup_gap).length;
  const poolAGaps     = sub_topics.filter((s) => s.pool_a_gap).length;
  const poolBGaps     = sub_topics.filter((s) => s.pool_b_gap).length;
  const coldPoolFlags = sub_topics.filter((s) => !s.cold_ratio_ok && s.expected_cold > 0).length;

  return (
    <div
      className="rounded-xl border overflow-hidden"
      style={{ borderColor: "#e5e7eb" }}
      data-testid={`grade-section-${gradeNum}`}
    >
      <button
        className="w-full flex items-center justify-between px-6 py-4 bg-white hover:bg-gray-50 transition-colors"
        onClick={() => setOpen((v) => !v)}
        data-testid={`grade-toggle-${gradeNum}`}
      >
        <div className="flex items-center gap-4 flex-wrap">
          <div className="text-base font-bold" style={{ color: "#111827", minWidth: 32 }}>
            {gradeNum}
          </div>
          <HealthBadge health={health} size="lg" />
          <span className="text-sm text-gray-500">
            {sub_topics.length} sub-topics · {subjectCount} subject{subjectCount !== 1 ? "s" : ""}
          </span>
          {warmupGaps > 0 && (
            <span className="text-xs text-red-600 font-semibold">{warmupGaps} WarmUp gap{warmupGaps !== 1 ? "s" : ""}</span>
          )}
          {poolAGaps > 0 && (
            <span className="text-xs text-purple-600 font-semibold">{poolAGaps} Pool A gap{poolAGaps !== 1 ? "s" : ""}</span>
          )}
          {poolBGaps > 0 && (
            <span className="text-xs font-semibold" style={{ color: "#b45309" }}>{poolBGaps} Pool B gap{poolBGaps !== 1 ? "s" : ""}</span>
          )}
          {coldPoolFlags > 0 && (
            <span className="text-xs text-amber-600 font-semibold">{coldPoolFlags} Cold Pool flag{coldPoolFlags !== 1 ? "s" : ""}</span>
          )}
        </div>
        <span className="text-gray-400" style={{ transform: open ? "rotate(180deg)" : "rotate(0)", transition: "transform 0.2s" }}>
          ▾
        </span>
      </button>

      {open && (
        <div style={{ borderTop: "1px solid #e5e7eb" }}>
          <SubTopicTable subTopics={sub_topics} onGenerate={onGenerate} />
        </div>
      )}
    </div>
  );
}

// ── Config version bar ────────────────────────────────────────────────────────

function ConfigVersionBar({ token }) {
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await apiFetch("/admin/cr/config-versions", token);
        if (res.ok) {
          const d = await res.json();
          setCfg(d);
        }
      } catch (_) {}
    })();
  }, [token]);

  if (!cfg) return null;

  const fmt = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "2-digit" }); }
    catch (_) { return iso; }
  };

  const schemaOk = cfg.schema?.exists;
  const regOk    = cfg.dep_registry?.exists;
  const valOk    = cfg.validator?.exists;
  const allOk    = schemaOk && regOk && valOk;

  return (
    <div
      className="flex items-center gap-4 px-4 py-2.5 rounded-lg text-xs flex-wrap"
      style={{ background: allOk ? "#f0fdf4" : "#fff7ed", borderColor: allOk ? "#6ee7b7" : "#fcd34d", border: "1px solid" }}
    >
      <span className="font-semibold text-gray-600">Config:</span>
      <span style={{ color: schemaOk ? "#065f46" : "#dc2626" }}>
        Schema v{cfg.schema?.version || "—"} {schemaOk ? "✓" : "✗"}
      </span>
      <span className="text-gray-400">|</span>
      <span style={{ color: regOk ? "#065f46" : "#dc2626" }}>
        Dep Registry {cfg.dep_registry?.total_codes || 0} codes {regOk ? "✓" : "✗"}
      </span>
      <span className="text-gray-400">|</span>
      <span style={{ color: valOk ? "#065f46" : "#dc2626" }}>
        Validator {valOk ? fmt(cfg.validator?.modified) : "missing ✗"}
      </span>
      {!allOk && (
        <span className="text-amber-700 font-semibold">
          — Generation disabled for missing config
        </span>
      )}
    </div>
  );
}

// ── Grade filter pills ────────────────────────────────────────────────────────

function GradeFilterPills({ grades, selected, onChange }) {
  const pills = ["All", ...grades.map((g) => g.grade).filter(Boolean).sort()];
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="grade-filter-pills">
      {pills.map((g) => {
        const isActive = g === selected;
        const label = g === "All" ? "All" : g.replace("Grade ", "G");
        return (
          <button
            key={g}
            onClick={() => onChange(g)}
            data-testid={`grade-pill-${label}`}
            className="px-3 py-1.5 rounded-full text-xs font-semibold transition-all"
            style={{
              background: isActive ? "#111827" : "#f3f4f6",
              color: isActive ? "#fff" : "#6b7280",
              border: isActive ? "1px solid #111827" : "1px solid #e5e7eb",
            }}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function DatasetHealth({ token, onUnauth }) {
  const [searchParams] = useSearchParams();
  const navigate        = useNavigate();

  // Scope from URL params (read once; stable across rerenders)
  const scopeBoard   = searchParams.get("board")   || "";
  const scopeGrade   = searchParams.get("grade")   || "";
  const scopeSubject = searchParams.get("subject") || "";
  const isScoped     = !!(scopeBoard || scopeGrade || scopeSubject);

  const [data,           setData]           = useState(null);
  const [loading,        setLoading]        = useState(true);
  const [error,          setError]          = useState("");
  const [generateInfo,   setGenerateInfo]   = useState(null); // {row, poolType}
  const [showBatch,      setShowBatch]      = useState(false);
  // Pre-select grade pill from URL param if present
  const [gradeFilter,    setGradeFilter]    = useState(scopeGrade || "All");
  const [clearConfirm,   setClearConfirm]   = useState(false);
  const [clearLoading,   setClearLoading]   = useState(false);
  const [clearResult,    setClearResult]    = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams();
      if (scopeBoard)   params.set("board",   scopeBoard);
      if (scopeGrade)   params.set("grade",   scopeGrade);
      if (scopeSubject) params.set("subject", scopeSubject);
      const qs = params.toString() ? `?${params}` : "";
      const res = await apiFetch(`/admin/cr/dataset-health${qs}`, token);
      if (res.status === 401) { onUnauth(); return; }
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || "Failed to load health data");
      setData(d);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, onUnauth, scopeBoard, scopeGrade, scopeSubject]);

  useEffect(() => { load(); }, [load]);

  const handleClearAllTests = async () => {
    setClearLoading(true);
    setClearResult(null);
    try {
      const res = await apiFetch("/admin/cr/tests/clear-all", token, { method: "DELETE" });
      if (res.status === 401) { onUnauth(); return; }
      const d = await res.json();
      setClearResult(d.deleted);
      setClearConfirm(false);
    } catch (err) {
      setClearResult({ error: err.message });
    } finally {
      setClearLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-64 text-sm text-gray-400">
        Loading dataset health...
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="text-sm text-red-600 bg-red-50 px-4 py-3 rounded-lg">{error}</div>
      </div>
    );
  }

  const { tita_count, grades = [], total_sub_topics } = data || {};

  // Apply grade filter
  const visibleGrades = gradeFilter === "All"
    ? grades
    : grades.filter((g) => g.grade === gradeFilter);

  const overallAmber  = visibleGrades.filter((g) => g.health === "amber").length;
  const warmupGapsAll = visibleGrades.reduce((s, g) => s + g.sub_topics.filter(st => st.warmup_gap).length, 0);
  const poolAGapsAll  = visibleGrades.reduce((s, g) => s + g.sub_topics.filter(st => st.pool_a_gap).length, 0);
  const poolBGapsAll  = visibleGrades.reduce((s, g) => s + g.sub_topics.filter(st => st.pool_b_gap).length, 0);
  const visibleSubTopics = visibleGrades.reduce((s, g) => s + (g.sub_topics || []).length, 0);

  // Build the list of gap items for "Generate All Gaps" (respects active filter)
  const allGapItems = [];
  visibleGrades.forEach(g => {
    (g.sub_topics || []).forEach(st => {
      if (st.warmup_gap) {
        allGapItems.push({ grade: st.grade, subject: st.subject, topic: st.topic, sub_topic: st.sub_topic, pool_type: "WarmUp" });
      }
      if (st.pool_a_gap && !isLitExempt(st)) {
        allGapItems.push({ grade: st.grade, subject: st.subject, topic: st.topic, sub_topic: st.sub_topic, pool_type: "Pool A" });
      }
      if (st.pool_b_gap && !isLitExempt(st)) {
        allGapItems.push({ grade: st.grade, subject: st.subject, topic: st.topic, sub_topic: st.sub_topic, pool_type: "Pool B" });
      }
    });
  });

  return (
    <div className="p-8" data-testid="dataset-health">
      {/* Scoped-mode banner */}
      {isScoped && (
        <div
          className="mb-5 flex items-center justify-between px-4 py-3 rounded-xl border border-indigo-200 bg-indigo-50 text-sm"
          data-testid="scoped-banner"
        >
          <div className="flex items-center gap-2 text-indigo-700 flex-wrap">
            <LayoutGrid className="w-4 h-4 flex-shrink-0" />
            <span className="font-semibold">Fuel Registry scope:</span>
            {scopeBoard   && <span className="bg-indigo-100 px-2 py-0.5 rounded font-mono text-xs">{scopeBoard}</span>}
            {scopeBoard   && (scopeGrade || scopeSubject) && <span className="text-indigo-400">·</span>}
            {scopeGrade   && <span className="text-indigo-600">{scopeGrade}</span>}
            {scopeGrade   && scopeSubject && <span className="text-indigo-400">·</span>}
            {scopeSubject && <span className="text-indigo-600">{scopeSubject}</span>}
          </div>
          <button
            onClick={() => navigate("/admin/dataset-health")}
            className="flex items-center gap-1 text-xs text-indigo-500 hover:text-indigo-700 font-medium transition-colors"
            data-testid="clear-scope-btn"
          >
            <X className="w-3.5 h-3.5" /> Clear scope
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dataset Health</h1>
          <p className="text-sm text-gray-500 mt-1">
            {gradeFilter === "All"
              ? `${total_sub_topics} sub-topics across ${grades.length} grades`
              : `${visibleSubTopics} sub-topics · ${gradeFilter}`}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {allGapItems.length > 0 && (
            <button
              onClick={() => setShowBatch(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white"
              style={{ background: "#7c3aed" }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#6d28d9")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "#7c3aed")}
              data-testid="generate-all-gaps-btn"
            >
              <Zap size={14} />
              Generate All Gaps
              <span className="ml-1 bg-white bg-opacity-20 px-1.5 py-0.5 rounded text-xs font-bold">
                {allGapItems.length}
              </span>
            </button>
          )}
          <button
            onClick={load}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border border-gray-200 text-gray-600 hover:bg-gray-100 transition-colors"
            data-testid="refresh-health-btn"
          >
            <RefreshCw size={14} />
            Refresh
          </button>
        </div>
      </div>

      {/* Config version bar */}
      <div className="mb-5">
        <ConfigVersionBar token={token} />
      </div>

      {/* Grade filter pills */}
      <div className="mb-6">
        <GradeFilterPills grades={grades} selected={gradeFilter} onChange={setGradeFilter} />
      </div>

      {/* Summary cards — reflect active filter */}
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-4 mb-8">
        <div className="rounded-xl border p-5 bg-white" style={{ borderColor: "#e5e7eb" }} data-testid="tita-count-card">
          <div className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">TITA Deferred</div>
          <div className="text-3xl font-bold text-gray-900">{tita_count}</div>
          <div className="text-xs text-gray-400 mt-1">questions (all grades)</div>
        </div>

        <div className="rounded-xl border p-5 bg-white" style={{ borderColor: "#e5e7eb" }}>
          <div className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">Sub-Topics</div>
          <div className="text-3xl font-bold text-gray-900">{visibleSubTopics}</div>
          <div className="text-xs text-gray-400 mt-1">
            {gradeFilter === "All" ? `across ${grades.length} grades` : gradeFilter}
          </div>
        </div>

        <div
          className="rounded-xl border p-5"
          style={{ background: warmupGapsAll > 0 ? "#dbeafe" : "#f9fafb", borderColor: warmupGapsAll > 0 ? "#93c5fd" : "#e5e7eb" }}
          data-testid="warmup-gaps-card"
        >
          <div className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: warmupGapsAll > 0 ? "#1e3a8a" : "#9ca3af" }}>
            WarmUp Gaps
          </div>
          <div className="text-3xl font-bold" style={{ color: warmupGapsAll > 0 ? "#1d4ed8" : "#374151" }}>
            {warmupGapsAll}
          </div>
          <div className="text-xs mt-1" style={{ color: warmupGapsAll > 0 ? "#1e40af" : "#9ca3af" }}>
            sub-topic{warmupGapsAll !== 1 ? "s" : ""} below min
          </div>
        </div>

        <div
          className="rounded-xl border p-5"
          style={{ background: poolAGapsAll > 0 ? "#ede9fe" : "#f9fafb", borderColor: poolAGapsAll > 0 ? "#c4b5fd" : "#e5e7eb" }}
          data-testid="pool-a-gaps-card"
        >
          <div className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: poolAGapsAll > 0 ? "#3b0764" : "#9ca3af" }}>
            Pool A Gaps
          </div>
          <div className="text-3xl font-bold" style={{ color: poolAGapsAll > 0 ? "#5b21b6" : "#374151" }}>
            {poolAGapsAll}
          </div>
          <div className="text-xs mt-1" style={{ color: poolAGapsAll > 0 ? "#6d28d9" : "#9ca3af" }}>
            sub-topic{poolAGapsAll !== 1 ? "s" : ""} below 5
          </div>
        </div>

        <div
          className="rounded-xl border p-5"
          style={{ background: poolBGapsAll > 0 ? "#fef3c7" : "#f9fafb", borderColor: poolBGapsAll > 0 ? "#fcd34d" : "#e5e7eb" }}
          data-testid="pool-b-gaps-card"
        >
          <div className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: poolBGapsAll > 0 ? "#78350f" : "#9ca3af" }}>
            Pool B Gaps
          </div>
          <div className="text-3xl font-bold" style={{ color: poolBGapsAll > 0 ? "#92400e" : "#374151" }}>
            {poolBGapsAll}
          </div>
          <div className="text-xs mt-1" style={{ color: poolBGapsAll > 0 ? "#b45309" : "#9ca3af" }}>
            sub-topic{poolBGapsAll !== 1 ? "s" : ""} below 5
          </div>
        </div>

        <div
          className="rounded-xl border p-5"
          style={{ background: overallAmber > 0 ? "#fffbeb" : "#f9fafb", borderColor: overallAmber > 0 ? "#fcd34d" : "#e5e7eb" }}
          data-testid="cold-pool-flags-card"
        >
          <div className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: overallAmber > 0 ? "#92400e" : "#9ca3af" }}>
            Cold Pool Flags
          </div>
          <div className="text-3xl font-bold" style={{ color: overallAmber > 0 ? "#d97706" : "#374151" }}>
            {overallAmber}
          </div>
          <div className="text-xs mt-1" style={{ color: overallAmber > 0 ? "#b45309" : "#9ca3af" }}>
            grade{overallAmber !== 1 ? "s" : ""} with ratio issues
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-5 mb-6 text-xs text-gray-500 flex-wrap">
        <span className="font-semibold text-gray-700">Legend:</span>
        <span><span className="font-bold" style={{ color: "#2563eb" }}>⚑ WarmUp</span> — minimum 2 (G1–G4) / 3 (G5+)</span>
        <span><span className="font-bold" style={{ color: "#7c3aed" }}>⚑ Pool A/B</span> — minimum 5 per pool</span>
        <span><span className="font-bold" style={{ color: "#059669" }}>⚑ Cold Pool</span> — target 2× Pool A + 2× Pool B</span>
        <span><span className="bg-indigo-50 text-indigo-600 px-1.5 rounded font-medium">Lit</span> — Literature sub-topic (Pool A/B Gen suppressed)</span>
      </div>

      {/* Grade sections */}
      <div className="space-y-4">
        {visibleGrades.map((gradeData) => (
          <GradeSection
            key={gradeData.grade}
            gradeData={gradeData}
            onGenerate={(row, poolType) => setGenerateInfo({ row, poolType })}
          />
        ))}
        {visibleGrades.length === 0 && (
          <div className="text-sm text-gray-400 text-center py-12">
            No data available for {gradeFilter}.
          </div>
        )}
      </div>

      {/* Cold Pool modal (existing) */}
      {generateInfo?.poolType === "Cold Pool" && (
        <GenerateModal
          row={generateInfo.row}
          token={token}
          onClose={() => setGenerateInfo(null)}
          onStored={() => { setGenerateInfo(null); load(); }}
        />
      )}

      {/* Universal modal for WarmUp / Pool A / Pool B */}
      {generateInfo && generateInfo.poolType !== "Cold Pool" && (
        <UniversalGenerateModal
          row={generateInfo.row}
          poolType={generateInfo.poolType}
          token={token}
          onClose={() => setGenerateInfo(null)}
          onStored={() => { setGenerateInfo(null); load(); }}
        />
      )}

      {/* Batch Generate All Gaps modal */}
      {showBatch && (
        <BatchGenerateModal
          gapItems={allGapItems}
          token={token}
          onClose={() => setShowBatch(false)}
          onStored={() => { setShowBatch(false); load(); }}
        />
      )}

      {/* ── Clear All Tests danger action ─────────────────────────────── */}
      <div className="mt-12 border-t border-red-100 pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-gray-700">Clear All Tests</p>
            <p className="text-xs text-gray-400 mt-0.5">
              Removes all published tests and assignments. Questions in pools are retained for future generation.
            </p>
          </div>
          <button
            onClick={() => { setClearConfirm(true); setClearResult(null); }}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-colors"
            style={{ background: "#dc2626" }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#b91c1c")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "#dc2626")}
            data-testid="clear-all-tests-btn"
          >
            <Trash2 size={14} /> Clear All Tests
          </button>
        </div>

        {/* Confirmation dialog */}
        {clearConfirm && (
          <div className="mt-4 p-4 rounded-xl border border-red-200 bg-red-50">
            <p className="text-sm font-semibold text-red-800 mb-3">
              This will permanently delete all test assignments, practice tests, and submissions. This cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleClearAllTests}
                disabled={clearLoading}
                className="px-4 py-1.5 rounded-lg text-sm font-semibold text-white"
                style={{ background: clearLoading ? "#9ca3af" : "#dc2626" }}
                data-testid="confirm-clear-tests-btn"
              >
                {clearLoading ? "Clearing..." : "Yes, clear all tests"}
              </button>
              <button
                onClick={() => setClearConfirm(false)}
                className="px-4 py-1.5 rounded-lg text-sm font-semibold border border-gray-300 text-gray-700 hover:bg-gray-100"
                data-testid="cancel-clear-tests-btn"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Result message */}
        {clearResult && !clearConfirm && (
          <div className="mt-4 p-3 rounded-xl border border-green-200 bg-green-50 text-sm text-green-800" data-testid="clear-tests-result">
            {clearResult.error
              ? <span className="text-red-700">Error: {clearResult.error}</span>
              : <>
                  Cleared: <strong>{clearResult.test_assignments}</strong> assignments,{" "}
                  <strong>{clearResult.practice_tests}</strong> tests,{" "}
                  <strong>{clearResult.test_submissions}</strong> submissions.
                  Question pools retained.
                </>
            }
          </div>
        )}
      </div>
    </div>
  );
}
